/**
 * Frozen Merge: aplica fresh data del backend al snapshot frozen.
 *
 * Reglas:
 * - mergedContacts: solo contiene filas que ya estaban en prevContacts (con
 *   datos refrescados de fresh donde existan). NO incluye nuevos prospectos;
 *   esos van por separado en newPhones[]; el caller los inserta arriba via
 *   markNewRow del store.
 * - Filas nuevas (no en snapshot Y no en prev) AND created_at < 24h:
 *   retornadas como newPhones; el caller las inserta al inicio del snapshot.
 *   El filtro de 24h es defensivo: contactos viejos que aparezcan en fresh
 *   por primera vez (ej: por scope filter change) NO deben tratarse como
 *   "nuevos" con highlight verde.
 * - Filas que desaparecen del fresh (no en fresh, pero si en snapshot/prev):
 *   YA NO son tracked como zombies. El caller (useFrozenMerge) hace drop
 *   silencioso filtrando por freshPhoneSet. Filas zombie eran fantasmas con
 *   strikethrough; el comportamiento nuevo es desaparición silenciosa.
 *
 * Cross-agent: si un row cambio sus campos clave (icono, fecha, flag, agente)
 * y el ultimo modificador NO es el currentAgent, retornar en crossAgentDots[].
 *
 * @param {string[]} snapshotPhones - orden congelado de phones
 * @param {Array} prevContacts - contacts actuales (con todos los campos)
 * @param {Array} freshContacts - respuesta del backend
 * @param {string} currentAgent - nombre del agente actual (ej: "Iván")
 * @param {number} [nowMs=Date.now()] - timestamp actual (inyectable para tests)
 * @returns {{ mergedContacts: Array, newPhones: string[], crossAgentDots: string[] }}
 */

const RECENT_THRESHOLD_MS = 24 * 60 * 60 * 1000

// Treat null and undefined as equivalent for change-detection. Backend may
// serialize a missing field as either; we don't want to fire a dot for that.
function fieldEqual(a, b) {
  const aN = a == null ? null : a
  const bN = b == null ? null : b
  return aN === bN
}

// Defensive: solo marcar como nuevo (highlight verde + scroll anchor) si fue
// creado en las últimas 24h. Contactos viejos que aparecen "nuevos" por cambio
// de scope/filter NO deben tener tratamiento de nuevo lead.
function isRecentlyCreated(contact, nowMs) {
  if (!contact || !contact.created_at) return false
  const createdMs = Date.parse(contact.created_at)
  if (Number.isNaN(createdMs)) return false
  return createdMs >= nowMs - RECENT_THRESHOLD_MS
}

export function mergeIntoSnapshot(snapshotPhones, prevContacts, freshContacts, currentAgent, nowMs = Date.now()) {
  // Defensive: ignore entries without a phone (malformed records).
  const validFresh = freshContacts.filter(c => c && c.phone)
  const validPrev = prevContacts.filter(c => c && c.phone)
  const freshByPhone = new Map(validFresh.map(c => [c.phone, c]))
  const prevByPhone = new Map(validPrev.map(c => [c.phone, c]))
  const snapshotSet = new Set(snapshotPhones)

  // 1. Updates in-place: iterar prev y aplicar datos frescos donde existan
  const mergedContacts = validPrev.map(prev => {
    const fr = freshByPhone.get(prev.phone)
    if (!fr) return prev // no hay fresh data, mantener
    return { ...prev, ...fr }
  })

  // 2. Detectar crossAgentDots: SOLO cambios de assigned_to (reasignacion).
  // Antes verificaba 6 campos (icon, fecha, bandera, assigned_to, hint, dia)
  // pero sin attribution real (backend no devuelve last_modified_by) -> el dot
  // se disparaba en edits del propio agente, generando ruido. Limitar a
  // assigned_to da un signal cross-agent limpio (reasignacion) sin needing
  // backend attribution. Si en el futuro queremos cross-agent edits de otros
  // campos, requerimos columna last_modified_by en prospect_crm_data.
  const crossAgentDots = []
  for (const fr of validFresh) {
    const prev = prevByPhone.get(fr.phone)
    if (!prev) continue
    if (fieldEqual(prev.assigned_to, fr.assigned_to)) continue
    crossAgentDots.push(fr.phone)
  }

  // 3. Detectar nuevos: en fresh pero no en snapshot/prev AND created_at < 24h.
  // El filtro de 24h evita que contactos viejos que entran al scope por primera
  // vez (ej: cambio de filtro) sean tratados como nuevos leads.
  const newPhones = []
  for (const fr of validFresh) {
    if (snapshotSet.has(fr.phone) || prevByPhone.has(fr.phone)) continue
    if (!isRecentlyCreated(fr, nowMs)) continue
    newPhones.push(fr.phone)
  }

  // 4. Zombies eliminados [2026-04-30]: el caller (useFrozenMerge) hace drop
  // silencioso filtrando por freshPhoneSet en vez de marcar zombies con
  // strikethrough. Mantenemos el contract de retorno sin el campo zombies.

  return { mergedContacts, newPhones, crossAgentDots }
}
